import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	isInProgressStreamResponse,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	CHAT_STORE_STATUS,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { clearStoreAssistantMessageContent } from './clearStoreAssistantMessageContent';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { extractAgentVersionFromMessages } from './extractAgentVersionFromMessages';
import { getConversation } from './getConversation';
import { getSourceAgentId } from './getSourceAgentId';
import { handleForbiddenConversation } from './handleForbiddenConversation';
import { hydrateConversationSpaces } from './hydrateConversationSpaces';
import { isEmptyConversation } from './isEmptyConversation';
import { isForbiddenConversationError } from './isForbiddenConversationError';
import { preserveTrailingFollowUps } from './preserveTrailingFollowUps';
import { processConversationMessages } from './processConversationMessages';
import { reconnectToInProgressStream } from './reconnectToInProgressStream';
import { resetConversationForLoading } from './resetConversationForLoading';
import { restoreTaskModeForConversation } from './restoreTaskModeForConversation';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ChatStoreAction, PrefetchConversationMessagesArgs } from './types';

/**
 * Imperative warm-path messages prefetch
 */
export const prefetchConversationMessages: SafeAction<[PrefetchConversationMessagesArgs]> =
	createSafeAction(
		StoreActionLoggableName.prefetchConversationMessages,
		({
			conversationId,
			skipNextFetch = false,
			onStart,
			onSuccess,
			onError,
		}: PrefetchConversationMessagesArgs): ChatStoreAction =>
			async (
				{ getState, dispatch },
				{
					assistanceService,
					experienceTracker,
					sendAnalyticsEvent,
					getMessages,
					setMessages,
					showFlag,
					capabilities,
					onConversationSpacesHydrated,
				},
			) => {
				const conversation = getState().conversations[conversationId];

				// Bail without emitting if the conversation isn't selectable, is
				// in flight, or is already loaded
				if (
					isEmptyConversation(conversationId) ||
					!conversation ||
					conversation.status !== CHAT_STORE_STATUS.idle ||
					conversation.messages.length > 0
				) {
					return;
				}

				onStart?.();
				experienceTracker.start({
					name: ExperienceName.PREFETCH_CHAT,
					id: conversationId,
					attributes: {
						conversationId,
					},
				});

				const messagesBeforeReload = getMessages(conversationId);

				dispatch(
					resetConversationForLoading({
						conversationId,
						traces: [],
						sourceAgentId: conversation.sourceAgentId ?? '',
					}),
				);

				try {
					if (isExperimentEnabled('rovo_chat_spaces_experiment')) {
						void assistanceService
							.fetchConversationMetadata(conversationId)
							.then((metadata) =>
								hydrateConversationSpaces({
									conversationId,
									metadata,
									dispatch,
									onConversationSpacesHydrated,
								}),
							)
							.catch(() => undefined);
					}
					const serviceChat = await assistanceService.getChat(conversationId, capabilities);

					const status = getState().conversations[conversationId]?.status;
					if (status !== CHAT_STORE_STATUS.loadingChat) {
						// Something else (e.g. a user-initiated send or an explicit
						// `fetchConversation`) raced ahead; surrender the response.
						return;
					}

					const messages = processConversationMessages({
						latestMessages: serviceChat,
						conversationId,
						sendAnalyticsEvent,
						shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
							getState(),
							conversationId,
						),
					});

					const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

					// Mirror fetchConversation's reconnect check.
					const lastMessage = serviceChat.at(-1);
					const isDeepResearch = (lastMessage as StreamResponse)?.message?.message?.message_metadata
						?.deep_research;
					const shouldReconnect = isInProgressStreamResponse(lastMessage);

					if (shouldReconnect) {
						const messageId = (lastMessage as StreamResponse).message.message.id;
						const updatedMessages = clearStoreAssistantMessageContent(messages, messageId);

						dispatch(
							setConversationData({
								conversationId,
								data: {
									...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
									sourceAgentId: getSourceAgentId(
										messages,
										getConversation(getState(), conversationId),
									),
									...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
									// We intentionally omit `skipNextFetch` here so
									// that if reconnect fails and the conversation returns to `idle`, a subsequent
									// `fetchConversation` call is NOT silenced by the flag and can retry normally.
									// `fetchConversation` is naturally blocked from re-running by the conversation
								},
							}),
						);

						// Do not preserve follow-ups while reconnecting: the resumed stream emits its own
						// FOLLOW_UP_QUERIES part, so re-attaching here could duplicate the chips.
						setMessages(conversationId, updatedMessages);
						dispatch(restoreTaskModeForConversation({ conversationId, messages }));

						dispatch(
							reconnectToInProgressStream({
								conversationId,
								messageId,
								isDeepResearch,
							}),
						);

						experienceTracker.succeed({ name: ExperienceName.PREFETCH_CHAT });
						onSuccess?.();
						return;
					}

					dispatch(
						setConversationData({
							conversationId,
							data: {
								...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
								sourceAgentId: getSourceAgentId(
									messages,
									getConversation(getState(), conversationId),
								),
								...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
								...(skipNextFetch ? { skipNextFetch: true } : {}),
							},
						}),
					);

					setMessages(
						conversationId,
						fg('rovo_chat_preserve_follow_ups_on_refetch')
							? preserveTrailingFollowUps(messagesBeforeReload, messages)
							: messages,
					);
					dispatch(restoreTaskModeForConversation({ conversationId, messages }));

					experienceTracker.succeed({ name: ExperienceName.PREFETCH_CHAT });
					onSuccess?.();
				} catch (error) {
					const normalizedError = error instanceof Error ? error : new Error(String(error));
					experienceTracker.fail({
						name: ExperienceName.PREFETCH_CHAT,
						error: normalizedError,
					});

					if (isForbiddenConversationError(error)) {
						handleForbiddenConversation({
							conversationId,
							currentConversationId: getState().currentConversationId,
							dispatch,
							sendAnalyticsEvent,
							showFlag,
						});
						onError?.(normalizedError);
						return;
					}

					// Roll back warm loading state; do not set `skipNextFetch` on failure.
					dispatch(
						setConversationData({
							conversationId,
							data: clearStatus({}),
						}),
					);
					onError?.(normalizedError);
				}
			},
		{
			experienceName: () => ExperienceName.PREFETCH_CHAT,
			actionErrorHandler: () => {
				// Errors are surfaced via `onError`; keep the silent warm path silent.
				return;
			},
		},
	);
